import { EmailReport } from "../atoms/EmailReportAtom";
import Configs from "../config";
import {
  CreateDraftEmailIRoute,
  DefaultCalendarPropsIRoute,
} from "../generated/Api";
import { IEmail, IEmailType } from "../types/EmailTypes";
import { ITagQuery } from "../types/TagQueryTypes";
import { DefaultResponse } from "../types/UtilTypes";
import { localStorageGetItem } from "../utils/LocalStorage";

export interface ICreateEmail {
  body: string;
  footerId: string;
  aliasId: string;
  labels: string[];
  subject: string;
  scheduleTo?: Date;
  toList?: string[];
  tagQuery?: ITagQuery;
  interestingURLs?: Record<string, string[]>;
  event?: DefaultCalendarPropsIRoute;
  searchId?: string;
  showOnArchive?: boolean;
  splitSubject?: string;
  splitMode?: "AGE" | "AGE_DOMAIN" | "DOMAIN";
  defaultModeColorLink?: string;
  darkModeColorLink?: string;
  resendMetadata?: any;
  isPersonal?: boolean;
}

interface ICreateEmailResponse {
  subject: string;
  body: string;
  fontSize: number;
  isFavorite: boolean;
  type: IEmailType;
  font: string;
  labels: string[];
  createdAt: Date;
  updatedAt: Date;
  emailOptions: {
    _id: string;
  };
  _id: string;
}

interface IUpdateEmail {
  body: string;
  font: string;
  subject: string;
  labels: string[];
  scheduleTo?: Date;
  tagQuery?: ITagQuery;
}

interface IReadEmail extends IEmail {
  _id: string;
}

interface IReadEmailTerm {
  emails: IReadEmail[];
  highlights?: string[];
}

interface IUpdateEmailResponse {
  count: number;
}

interface IDeleteEmail {
  deleted: number;
}

export const getLinkPreviews = async (urls: string[]) => {
  const token = localStorageGetItem("USER_TOKEN");
  const response = await fetch(`${Configs.SERVER_URL}/email/link`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({
      urls,
    }),
  });

  return (await response.json()).data as {
    link: string;
    valid: boolean;
    previewImageId: string | null;
  }[];
};

export const createDraftEmail = async (data: ICreateEmail) => {
  const userUid = localStorageGetItem("USER_UID");
  const token = localStorageGetItem("USER_TOKEN");
  const response = await fetch(`${Configs.SERVER_URL}/email/draft`, {
    method: "post",
    headers: {
      Accept: "application/json",
      "Content-type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({ ...data, userUid, type: "draft" }),
  });

  return response.json();
};

export const createEmail = async (
  {
    body,
    footerId,
    aliasId,
    labels,
    subject,
    scheduleTo,
    toList,
    tagQuery,
    interestingURLs,
    event,
    searchId,
    showOnArchive,
    splitSubject,
    splitMode,
    defaultModeColorLink,
    darkModeColorLink,
    resendMetadata,
    isPersonal,
  }: ICreateEmail,
  type: IEmailType
): Promise<ICreateEmailResponse> => {
  const userUid = localStorageGetItem("USER_UID");
  const token = localStorageGetItem("USER_TOKEN");

  const sanitizedInterestTags = { ...(interestingURLs || {}) };
  if (interestingURLs) {
    Object.keys(interestingURLs).forEach((tag) => {
      delete sanitizedInterestTags[tag];
      sanitizedInterestTags[tag.split(".").join("|")] = interestingURLs[tag];
    });
  }

  const response = await fetch(`${Configs.SERVER_URL}/email`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({
      body,
      footerId,
      aliasId,
      labels,
      subject,
      isFavorite: false,
      tagQuery,
      scheduleTo,
      interestingURLs: sanitizedInterestTags,
      toList,
      userUid,
      type,
      event,
      searchId,
      showOnArchive,
      splitSubject,
      splitMode,
      defaultModeColorLink,
      darkModeColorLink,
      resendMetadata,
      isPersonal,
    }),
  });

  const emails: DefaultResponse<ICreateEmailResponse> = await response.json();
  if (emails.status === "ok") {
    return emails.data as ICreateEmailResponse;
  }

  throw new Error(emails.code);
};

export const readEmail = async (id: string): Promise<IReadEmail> => {
  const token = localStorageGetItem("USER_TOKEN");
  const response = await fetch(`${Configs.SERVER_URL}/email/${id}`, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Content-type": "application/json",
      Authorization: "Bearer " + token,
    },
  });

  const emails: DefaultResponse<IReadEmail> = await response.json();
  if (emails.status === "ok") {
    return emails.data as IReadEmail;
  }

  throw new Error(emails.code);
};

export const readAllEmails = async (options?: {
  fromEmailSortDate?: string;
  limit?: number;

  filters?: {
    scheduled: boolean;
    sent: boolean;
    cancelled: boolean;
    labels: string[];
    search: string;
  };

  orderBy?: "MOST_RECENT" | "OLDEST";

  filterBy?: "NEXT_WEEK" | "NEXT_DAY" | "NEXT_MONTH" | "ALL_TIME" | "RANGE";

  dateRange?: {
    from: Date;
    to: Date;
  };
}): Promise<IReadEmailTerm> => {
  if (!options) options = {};
  const { fromEmailSortDate, limit, filters, orderBy, filterBy, dateRange } =
    options;

  const userUid = localStorageGetItem("USER_UID");
  const token = localStorageGetItem("USER_TOKEN");
  const response = await fetch(`${Configs.SERVER_URL}/email/filters`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({
      userUid,
      fromEmailSortDate,
      limit,
      filters,
      orderBy,
      filterBy,
      dateRange,
    }),
  });

  const emails: DefaultResponse<IReadEmailTerm> = await response.json();
  if (emails.status === "ok") {
    return emails.data as IReadEmailTerm;
  }

  throw new Error(emails.code);
};

const readAllEmailsCount = async (): Promise<{ count: number }> => {
  const token = localStorageGetItem("USER_TOKEN");
  const response = await fetch(`${Configs.SERVER_URL}/email/userUid/count//`, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Content-type": "application/json",
      Authorization: "Bearer " + token,
    },
  });

  const emails: DefaultResponse<{ count: number }> = await response.json();
  if (emails.status === "ok") {
    return emails.data as { count: number };
  }

  throw new Error(emails.code);
};

export const updateEmail = async (
  emailId: string,
  email: any
): Promise<IUpdateEmailResponse> => {
  // if (email["contactsSentTo"] && !email["contactsSentTo"].length) delete email["contactsSentTo"];
  // else delete email.contactsSentTo

  const emailShallowCopy = { ...email };

  const token = localStorageGetItem("USER_TOKEN");

  if (emailShallowCopy.interestingURLs) {
    const interestingURLs = emailShallowCopy.interestingURLs;
    const sanitizedInterestTags = { ...(interestingURLs || {}) };
    if (interestingURLs) {
      Object.keys(interestingURLs).forEach((tag) => {
        delete sanitizedInterestTags[tag];
        sanitizedInterestTags[tag.split(".").join("|")] = interestingURLs[tag];
      });
    }

    emailShallowCopy.interestingURLs = sanitizedInterestTags;
  }

  const response = await fetch(`${Configs.SERVER_URL}/email/${emailId}`, {
    method: "PUT",
    headers: {
      Accept: "application/json",
      "Content-type": "application/json",
      Authorization: "Bearer " + token,
    },

    body: JSON.stringify({ ...email, contactsSentTo: undefined }),
  });

  const emails: DefaultResponse<IUpdateEmailResponse> = await response.json();

  if (emails.status === "ok") {
    return emails.data as IUpdateEmailResponse;
  }

  throw new Error(emails.code);
};

export const updateDraftEmail = async (
  emailId: string,
  email: any
): Promise<IUpdateEmailResponse> => {
  // if (email["contactsSentTo"] && !email["contactsSentTo"].length) delete email["contactsSentTo"];
  // else delete email.contactsSentTo

  const token = localStorageGetItem("USER_TOKEN");
  const response = await fetch(`${Configs.SERVER_URL}/email/draft/${emailId}`, {
    method: "PUT",
    headers: {
      Accept: "application/json",
      "Content-type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({ ...email, contactsSentTo: undefined }),
  });

  const emails: DefaultResponse<IUpdateEmailResponse> = await response.json();
  if (emails.status === "ok") {
    return emails.data as IUpdateEmailResponse;
  }

  throw new Error(emails.code);
};

export const deleteEmail = async (emailId: string): Promise<IDeleteEmail> => {
  const token = localStorageGetItem("USER_TOKEN");
  const response = await fetch(`${Configs.SERVER_URL}/email/${emailId}`, {
    method: "DELETE",
    headers: {
      Authorization: "Bearer " + token,
    },
  });

  const emails: DefaultResponse<IDeleteEmail> = await response.json();
  if (emails.status === "ok") {
    return emails.data as IDeleteEmail;
  }

  throw new Error(emails.code);
};

export const getEmailSummaryByHash = async (
  emailHash: string,
  signal?: AbortSignal
): Promise<{
  summary: EmailReport;
  summaryA?: EmailReport;
  summaryB?: EmailReport;
}> => {
  const token = localStorageGetItem("USER_TOKEN");
  const response = await fetch(
    `${Configs.SERVER_URL}/email/summary/${emailHash}`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-type": "application/json",
        Authorization: "Bearer " + token,
      },
      signal,
    }
  );

  const emails: DefaultResponse<{
    summary: EmailReport;
    summaryA?: EmailReport;
    summaryB?: EmailReport;
  }> = await response.json();
  if (emails.status === "ok") {
    return {
      summary: emails.data.summary,
      summaryA: emails.data.summaryA,
      summaryB: emails.data.summaryB,
    };
  }

  throw new Error(emails.code);
};

export const getLightEmailSummaryByHash = async (
  emailHash: string,
  signal?: AbortSignal
): Promise<{
  summary: EmailReport;
  summaryA?: EmailReport;
  summaryB?: EmailReport;
}> => {
  const token = localStorageGetItem("USER_TOKEN");
  const response = await fetch(
    `${Configs.SERVER_URL}/email/light_summary/${emailHash}`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-type": "application/json",
        Authorization: "Bearer " + token,
      },
      signal,
    }
  );

  const emails: DefaultResponse<{
    summary: EmailReport;
    summaryA?: EmailReport;
    summaryB?: EmailReport;
  }> = await response.json();
  if (emails.status === "ok") {
    return {
      summary: emails.data.summary,
      summaryA: emails.data.summaryA,
      summaryB: emails.data.summaryB,
    };
  }

  throw new Error(emails.code);
};

const getContactsCount = async (tagQuery: ITagQuery, signal?: AbortSignal) => {
  try {
    const userUid = localStorageGetItem("USER_UID");
    const token = localStorageGetItem("USER_TOKEN");

    const response = await fetch(`${Configs.SERVER_URL}/email/contact/count`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-type": "application/json",
        Authorization: "Bearer " + token,
      },
      body: JSON.stringify({ userUid, tagQuery }),
      signal,
    });

    const responseObject = await response.json();
    if (responseObject.status === "ok") {
      return responseObject.data.contactsCount;
    }

    throw new Error(responseObject.code);
  } catch (e) {}
};

export const checkScheduleTimeSimilarity = async (timestamp: Date) => {
  const token = localStorageGetItem("USER_TOKEN");
  const userUid = localStorageGetItem("USER_UID");
  const response = await fetch(`${Configs.SERVER_URL}/email/similar_time`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({
      userUid,
      timestamp,
    }),
  });

  const isSimilar: DefaultResponse<boolean> = await response.json();

  if (isSimilar.status === "ok") {
    return isSimilar.data;
  }

  throw new Error(isSimilar.code);
};

export const EmailAPI = {
  createEmail,
  readEmail,
  readAllEmails,
  updateEmail,
  deleteEmail,
  getEmailSummaryByHash,
  getLightSummaryByHash: getLightEmailSummaryByHash,
  readAllEmailsCount,
  getContactsCount,
};
